import { secondaryPalette } from '@paintscout/util'

/**
 * Crew color palette: a thin lookup over `secondaryPalette` (the design system's
 * pre-tokenized color buckets) so each card uses the *exact* hex values from
 * the `<hue>/CXX` chart instead of computing them from the crew hex.
 *
 *   <hue>/C40  -> accent       (title, price, dot separator)
 *   <hue>/C50  -> accentSoft   (crew badge bg, subtitle, hours)
 *   <hue>/C60  -> progressFill (progress bar fill — same as the crew color itself)
 *   <hue>/C90  -> surface      (card body)
 *   <hue>/C95  -> track        (progress bar track — lighter than the surface so the
 *                               unfilled area reads as a soft channel against C90)
 *   <hue>/C95  -> chipBg       (sales-calendar event chip background)
 *
 * User and crew colors are always set from the design system palette, so we
 * just match the input hex against any C-step (C30..C95) of any bucket and
 * return that bucket's tokens directly.
 */

type Bucket = (typeof secondaryPalette)[keyof typeof secondaryPalette]

const FALLBACK_BUCKET: Bucket = secondaryPalette['260']

const BUCKETS_BY_HEX: Record<string, Bucket> = {}
Object.values(secondaryPalette).forEach((bucket) => {
  Object.values(bucket).forEach((hex) => {
    if (typeof hex === 'string' && hex.startsWith('#')) {
      BUCKETS_BY_HEX[hex.toUpperCase()] = bucket
    }
  })
})

const findBucket = (hex: string | undefined | null): Bucket => {
  if (!hex) return FALLBACK_BUCKET
  return BUCKETS_BY_HEX[hex.toUpperCase()] ?? FALLBACK_BUCKET
}

export interface CrewPalette {
  /** C95 — chip body (very light tint, used by sales-calendar Day/Week event chips) */
  chipBg: string
  /** C90 — card background */
  surface: string
  /** Same as surface (chart does not define a hover state) */
  surfaceHover: string
  /** C95 — progress bar track (lighter than surface so the unfilled area reads as a soft channel) */
  track: string
  /** C60 — progress bar fill (= the crew color itself) */
  progressFill: string
  /** C50 — crew badge background, subtitle, hours */
  accentSoft: string
  /** C40 — title, price, dot separator (darkest text) */
  accent: string
}

export function getCrewPalette(hex: string | undefined | null): CrewPalette {
  const bucket = findBucket(hex)
  return {
    accent: bucket.C40,
    accentSoft: bucket.C50,
    progressFill: bucket.C60,
    surface: bucket.C90,
    surfaceHover: bucket.C90,
    track: bucket.C95,
    chipBg: bucket.C95
  }
}
